import { Button } from '@paulownia/shared/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@paulownia/shared/components/ui/dialog';
import { ShoppingCart, Plus, Minus, Trash2, ChevronRight } from 'lucide-react';
import { getCompositionOption, isCompositionSelection, getEffectiveSaleMode, isUnitCountSaleMode, safeToFixed, type CompositionSelection, type Product } from '@paulownia/shared/types';
import type { CartModalProps, CartItem } from './types';
import { calculateEstimatedPrice } from './sale-mode-utils';

// Calculate total price modifier from selected options in a cart item
function calculateCartItemOptionsModifier(item: CartItem, product: Product | undefined): number {
    if (!item.options || !product?.availableOptions) return 0;

    let total = 0;

    for (const itemOption of item.options) {
        const productOption = product.availableOptions.find(o => o.id === itemOption.id);
        if (!productOption || !productOption.choices) continue;

        if (productOption.type === 'select' && typeof itemOption.value === 'string') {
            const choice = productOption.choices.find(c => c.id === itemOption.value);
            if (choice?.priceModifier) {
                total += choice.priceModifier;
            }
        } else if (productOption.type === 'multiselect' && Array.isArray(itemOption.value)) {
            for (const choiceId of itemOption.value) {
                if (typeof choiceId === 'string') {
                    const choice = productOption.choices.find(c => c.id === choiceId);
                    if (choice?.priceModifier) {
                        total += choice.priceModifier;
                    }
                }
            }
        }
    }

    return total;
}

export function CartModal({
    open,
    onOpenChange,
    cart,
    products,
    total,
    onUpdateQuantity,
    onToggleOption,
    onCheckout,
}: CartModalProps) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-bold flex items-center gap-3">
                        <ShoppingCart className="w-6 h-6" />
                        Mon Panier
                    </DialogTitle>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto py-4 space-y-4">
                    {cart.length === 0 ? (
                        <div className="flex flex-col items-center justify-center text-gray-400 space-y-4 py-12">
                            <ShoppingCart className="w-16 h-16" />
                            <p className="text-lg font-medium">Votre panier est vide</p>
                        </div>
                    ) : (
                        cart.map((item, index) => {
                            const product = products.find(p => p.id === item.productId);
                            const availableOptions = product?.availableOptions || [];
                            const hasPortionSize = !!item.portionSizeId;
                            const saleMode = item.saleMode || (product ? getEffectiveSaleMode(product) : 'UNIT');
                            const isEstimatedPrice = saleMode === 'UNIT_PRICED_BY_WEIGHT' || saleMode === 'WEIGHT_ESTIMATED';

                            // Calculate line total using the correct formula for each sale mode
                            const basePrice = product
                                ? calculateEstimatedPrice(
                                    product,
                                    saleMode,
                                    item.quantity,
                                    item.servings || 0,
                                    item.portionSizeId || null
                                  )
                                : null;
                            // Add options price modifiers (per unit for unit-based modes)
                            const optionsModifier = calculateCartItemOptionsModifier(item, product);
                            const optionsTotal = isUnitCountSaleMode(saleMode)
                                ? optionsModifier * item.quantity
                                : optionsModifier;
                            const lineTotal = basePrice !== null ? basePrice + optionsTotal : null;

                            // Check for composition
                            const compositionOption = product ? getCompositionOption(product) : undefined;
                            const compositionLineOpt = compositionOption
                                ? item.options.find(opt => opt.id === compositionOption.id)
                                : undefined;
                            const compositionSelections: CompositionSelection[] =
                                compositionLineOpt && isCompositionSelection(compositionLineOpt.value)
                                    ? compositionLineOpt.value
                                    : [];
                            const hasComposition = compositionOption && compositionSelections.length > 0;

                            return (
                                <div key={`${item.productId}-${index}`} className="bg-white p-4 rounded-lg border border-gray-200 space-y-3">
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                            <h4 className="font-bold text-gray-900 text-base leading-tight">{item.productName}</h4>
                                            {/* Show portion size label if applicable */}
                                            {item.portionSizeLabel && (
                                                <span className="text-sm text-orange-600 font-medium">
                                                    {item.portionSizeLabel}
                                                </span>
                                            )}
                                            {/* Show servings for WEIGHT_ESTIMATED */}
                                            {saleMode === 'WEIGHT_ESTIMATED' && item.servings && (
                                                <span className="text-sm text-orange-600 font-medium">
                                                    {item.servings} pers · ≈{safeToFixed(item.quantity, 2).replace('.', ',')} kg
                                                </span>
                                            )}
                                            {/* Show composition details */}
                                            {hasComposition && (
                                                <div className="text-xs text-purple-700 mt-1">
                                                    {compositionSelections
                                                        .filter(s => s.quantity > 0)
                                                        .map(selection => {
                                                            const componentProduct = products.find(p => p.id === selection.productId);
                                                            const component = compositionOption?.components?.find(c => c.productId === selection.productId);
                                                            const label = componentProduct?.name || component?.label || 'Inconnu';
                                                            return `${selection.quantity}× ${label}`;
                                                        })
                                                        .join(', ')}
                                                </div>
                                            )}
                                        </div>
                                        {lineTotal !== null && Number.isFinite(lineTotal) && (
                                            <span className="font-bold text-gray-900">
                                                {isEstimatedPrice && '≈ '}
                                                {safeToFixed(lineTotal, 2)}€
                                            </span>
                                        )}
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                                            <button
                                                onClick={() => onUpdateQuantity(index, -1)}
                                                className="w-8 h-8 flex items-center justify-center bg-white rounded-md shadow-sm text-gray-600 hover:text-red-600 active:scale-95 transition-all"
                                            >
                                                {/* Show trash when quantity would go to 0 */}
                                                {item.quantity <= (hasPortionSize || saleMode === 'UNIT' || saleMode === 'UNIT_PRICED_BY_WEIGHT' || saleMode === 'WEIGHT_PACKAGED' ? 1 : 0.1) ? (
                                                    <Trash2 className="w-4 h-4" />
                                                ) : (
                                                    <Minus className="w-4 h-4" />
                                                )}
                                            </button>
                                            <span className="w-16 text-center font-bold text-gray-900 text-sm">
                                                {/* Show decimal for weight-based products, integer for others */}
                                                {(saleMode === 'WEIGHT' || saleMode === 'WEIGHT_ESTIMATED') && !hasPortionSize
                                                    ? safeToFixed(item.quantity, 2)
                                                    : item.quantity}
                                                {(saleMode === 'WEIGHT' || saleMode === 'WEIGHT_ESTIMATED') && !hasPortionSize && (
                                                    <span className="text-xs text-gray-500 font-normal ml-0.5">kg</span>
                                                )}
                                            </span>
                                            <button
                                                onClick={() => onUpdateQuantity(index, 1)}
                                                className="w-8 h-8 flex items-center justify-center bg-white rounded-md shadow-sm text-gray-600 hover:text-green-600 active:scale-95 transition-all"
                                            >
                                                <Plus className="w-4 h-4" />
                                            </button>
                                        </div>

                                        {/* Dynamic Options */}
                                        {availableOptions.length > 0 && (
                                            <div className="flex gap-1 flex-wrap">
                                                {availableOptions.map(option => {
                                                    const lineOption = item.options.find(opt => opt.id === option.id);

                                                    // Boolean option - toggleable
                                                    if (option.type === 'boolean') {
                                                        const isActive = lineOption?.value === true;
                                                        return (
                                                            <button
                                                                key={option.id}
                                                                onClick={() => onToggleOption(index, option.id)}
                                                                className={`px-2 py-1 text-xs font-medium rounded-lg border transition-colors ${
                                                                    isActive
                                                                        ? 'bg-red-50 border-red-200 text-red-700'
                                                                        : 'bg-white border-gray-200 text-gray-400 hover:border-gray-300'
                                                                }`}
                                                            >
                                                                {option.label}
                                                            </button>
                                                        );
                                                    }

                                                    // Select option - show selected choice (read-only)
                                                    if (option.type === 'select' && typeof lineOption?.value === 'string') {
                                                        const selectedChoice = option.choices?.find(c => c.id === lineOption.value);
                                                        if (selectedChoice) {
                                                            return (
                                                                <span
                                                                    key={option.id}
                                                                    className="px-2 py-1 text-xs font-medium rounded-lg bg-orange-50 border border-orange-200 text-orange-700"
                                                                >
                                                                    {option.label}: {selectedChoice.label}
                                                                </span>
                                                            );
                                                        }
                                                    }

                                                    // Multiselect option - show selected choices (read-only)
                                                    if (option.type === 'multiselect' && Array.isArray(lineOption?.value) && lineOption.value.length > 0) {
                                                        const selectedChoices = lineOption.value
                                                            .map(choiceId => option.choices?.find(c => c.id === choiceId)?.label)
                                                            .filter(Boolean);
                                                        if (selectedChoices.length > 0) {
                                                            return (
                                                                <span
                                                                    key={option.id}
                                                                    className="px-2 py-1 text-xs font-medium rounded-lg bg-blue-50 border border-blue-200 text-blue-700"
                                                                >
                                                                    {option.label}: {selectedChoices.join(', ')}
                                                                </span>
                                                            );
                                                        }
                                                    }

                                                    return null;
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
                {cart.length > 0 && (
                    <div className="border-t pt-4 space-y-4">
                        <div className="flex justify-between items-end">
                            <span className="text-gray-500 font-medium">Total à payer</span>
                            <span className="text-2xl font-bold text-gray-900">{safeToFixed(total, 2)} €</span>
                        </div>
                        <Button
                            size="lg"
                            className="w-full h-12 text-lg font-bold rounded-xl bg-green-600 hover:bg-green-700 active:scale-95 transition-all"
                            onClick={onCheckout}
                        >
                            Commander
                            <ChevronRight className="w-5 h-5 ml-2" />
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
